import { Box, Divider, Typography, Tooltip } from '@material-ui/core'
import InfoIcon from '@material-ui/icons/Info'
import currency from 'currency.js'
import React from 'react'
import styled from 'styled-components'

import PriceBreakdown from '../checkout/PriceBreakdown'
import { formatCurrencyInCents } from '../../util/formatUtils'
import CartMenuItemList from './CartMenuItemList'

const CartContainer = styled.div`
  padding-top: 0px;
`

const YourOrder = styled.div`
  font-size: 20px;
  padding-bottom: 10px;
  font-weight: 400;
  line-height: 23px;
`

export const OrderCart = ({
  cart: { itemSubTotal, orderItems },
  deleteMenuItem,
  isCartSaving,
  editMenuItem,
  isLoadingOrderForEdit,
  isEditingPastOrder,
  orderMinimumInCents,
  showQuantity,
  deliveryFee,
}) => {
  const deliveryFeeInDollars = deliveryFee / 100

  function setUndefinedItemSubtotalToZero(value) {
    return Number.isNaN(value) || value === undefined ? 0 : value
  }

  const reserveFeeNotice =
    'The reserve fee ensures exclusive delivery to your location at a set time and varies by restaurant choice and other factors.'

  return (
    <CartContainer>
      <YourOrder>Your Order</YourOrder>
      <CartMenuItemList
        deleteMenuItem={deleteMenuItem}
        isDeletingMenuItem={isCartSaving}
        editable
        editMenuItem={editMenuItem}
        isEditingMenuItem={isLoadingOrderForEdit}
        isEditingPastOrder={isEditingPastOrder}
        menuItems={orderItems}
        showEditLinks
        useFullSubTotal
        showQuantity={showQuantity}
      />
      <Box marginBottom={2} marginTop={3}>
        <Divider />
      </Box>
      {deliveryFee && (
        <>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box display="flex" justifyContent="center" alignItems="center">
              <p style={{ fontSize: '14px', marginRight: '5px' }}> Reserve Fee </p>
              <Tooltip disableFocusListener disableTouchListener title={reserveFeeNotice}>
                <InfoIcon fontSize="small" style={{ color: 'ABADB2', lineHeight: '14px' }} />
              </Tooltip>
            </Box>
            <p style={{ fontSize: '14px' }}>
              {currency(deliveryFeeInDollars, { formatWithSymbol: true }).format()}
            </p>
          </Box>
          <Box marginBottom={2} marginTop={3}>
            <Divider />
          </Box>
        </>
      )}
      <Box display="flex" justifyContent="space-between">
        <Typography variant="body1" fontWeight="bold">
          Meal Total
        </Typography>

        <Typography fontSize={30} fontWeight={700}>
          {deliveryFee
            ? currency(setUndefinedItemSubtotalToZero(itemSubTotal) + deliveryFeeInDollars, {
                formatWithSymbol: true,
              }).format()
            : currency(itemSubTotal, { formatWithSymbol: true }).format()}
        </Typography>
      </Box>
      {orderMinimumInCents && (
        <Box display="flex" justifyContent="right">
          <Typography variant="body2" color="textSecondary">
            {`${formatCurrencyInCents(orderMinimumInCents)} minimum`}
          </Typography>
        </Box>
      )}
    </CartContainer>
  )
}

export const CheckoutCart = ({
  cart: { foodsbyFee, orderItems, orderTotal, preTaxSubTotal, taxSubTotal, taxSubtotal }, //again, need to support but subTotal spellings :(
  couponUsage,
  creditUsage,
  deleteMenuItem,
  isCartSaving,
  editMenuItem,
  isLoadingOrderForEdit,
  isEditingPastOrder,
  showQuantity,
  programUsage = {},
  storedCurrencyUsage = {},
}) => {
  return (
    <CartContainer>
      <Typography gutterBottom variant="overline">
        Your order
      </Typography>
      <CartMenuItemList
        deleteMenuItem={deleteMenuItem}
        isDeletingMenuItem={isCartSaving}
        editable
        editMenuItem={editMenuItem}
        isEditingMenuItem={isLoadingOrderForEdit}
        isEditingPastOrder={isEditingPastOrder}
        menuItems={orderItems}
        showEditLinks
        useFullSubTotal
        showQuantity={showQuantity}
      />
      <Box marginY={3}>
        <Divider />
      </Box>
      <PriceBreakdown
        couponAmount={couponUsage.couponAmount}
        couponApplied={!couponUsage.isEpp && couponUsage.couponApplied}
        couponCode={couponUsage.couponCode}
        creditsApplied={creditUsage.creditsApplied}
        creditsUsed={creditUsage.creditBalanceUsed}
        orderFee={foodsbyFee}
        subtotal={preTaxSubTotal}
        programAmount={programUsage?.programAmount || couponUsage.couponAmount}
        programApplied={
          programUsage?.programApplied || (couponUsage.couponApplied && couponUsage.isEpp)
        }
        programCode={programUsage?.programName || couponUsage.couponCode}
        storedCurrencyAmount={storedCurrencyUsage?.storedCurrencyBalanceUsed}
        storedCurrencyApplied={storedCurrencyUsage?.storedCurrencyApplied}
        taxes={taxSubTotal || taxSubtotal}
        orderTotal={orderTotal}
      />
    </CartContainer>
  )
}

export const ReserveCheckoutCart = ({
  cart: {
    foodsbyFee,
    orderItems,
    orderTotal,
    preTaxSubTotal,
    taxSubTotal,
    taxSubtotal,
    deliveryFeeContribution,
  }, //again, need to support but subTotal spellings :(
  couponUsage,
  creditUsage,
  deleteMenuItem,
  isCartSaving,
  editMenuItem,
  isLoadingOrderForEdit,
  isEditingPastOrder,
  showQuantity,
  programUsage = {},
  storedCurrencyUsage = {},
}) => {
  return (
    <CartContainer>
      <Typography gutterBottom variant="overline">
        Your order
      </Typography>
      <CartMenuItemList
        deleteMenuItem={deleteMenuItem}
        isDeletingMenuItem={isCartSaving}
        editable
        editMenuItem={editMenuItem}
        isEditingMenuItem={isLoadingOrderForEdit}
        isEditingPastOrder={isEditingPastOrder}
        menuItems={orderItems}
        showEditLinks
        useFullSubTotal
        showQuantity={showQuantity}
      />

      <PriceBreakdown
        couponAmount={couponUsage.couponAmount}
        couponApplied={!couponUsage.isEpp && couponUsage.couponApplied}
        couponCode={couponUsage.couponCode}
        creditsApplied={creditUsage.creditsApplied}
        creditsUsed={creditUsage.creditBalanceUsed}
        orderFee={foodsbyFee}
        subtotal={preTaxSubTotal}
        programAmount={programUsage?.programAmount || couponUsage.couponAmount}
        programApplied={
          programUsage?.programApplied || (couponUsage.couponApplied && couponUsage.isEpp)
        }
        programCode={programUsage?.programName || couponUsage.couponCode}
        storedCurrencyAmount={storedCurrencyUsage?.storedCurrencyBalanceUsed}
        storedCurrencyApplied={storedCurrencyUsage?.storedCurrencyApplied}
        taxes={taxSubTotal || taxSubtotal}
        orderTotal={orderTotal}
        deliveryFeeContribution={deliveryFeeContribution}
      />
    </CartContainer>
  )
}
